import React from 'react';
import { Seo, breadcrumbJsonLd } from '@/components/Seo';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Star, MessageSquare, ExternalLink, Quote } from 'lucide-react';
import { SITE } from '@/data/site-config';
import { REVIEWS, GOOGLE_REVIEWS_URL, RATING_ONLY_FALLBACK } from '@/data/reviews';

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={16} className="text-accent" fill="currentColor" strokeWidth={0} />
      ))}
    </div>
  );
}

export default function ReviewsPage() {
  const path = '/reviews';
  const breadcrumbs = [{ name: 'Home', path: '/' }, { name: 'Reviews', path }];

  const avg = REVIEWS.length
    ? (REVIEWS.reduce((a, r) => a + r.rating, 0) / REVIEWS.length).toFixed(1)
    : null;

  return (
    <>
      <Seo
        title="Client Reviews"
        description={`Real Google reviews for ${SITE.name} — private personal training in ${SITE.city}, ${SITE.stateAbbr}.`}
        canonical={path}
        jsonLd={breadcrumbJsonLd(breadcrumbs)}
      />
      <Breadcrumbs items={breadcrumbs} />

      <section className="container-x py-10">
        <div className="max-w-3xl">
          <span className="eyebrow">Client reviews</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-3" data-testid="reviews-page-h1">
            What clients say about Fitness Training 360.
          </h1>
          <p className="text-[color:var(--ft-muted)] text-lg mt-4">
            Real reviews from real clients on Google. Every review below is published exactly as written.
          </p>

          {avg && (
            <div className="mt-6 flex items-center gap-4" data-testid="reviews-aggregate">
              <div className="flex items-center gap-2">
                <Stars count={5} />
                <span className="font-semibold text-lg">{avg}</span>
              </div>
              <span className="text-[color:var(--ft-muted)]">
                Based on {REVIEWS.length} Google review{REVIEWS.length === 1 ? '' : 's'}
              </span>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              data-testid="reviews-page-google-cta"
            >
              Read all reviews on Google <ExternalLink size={16} />
            </a>
            <a
              href={`mailto:${SITE.email}?subject=Review for Fitness Training 360`}
              className="btn btn-secondary"
              data-testid="reviews-page-send-review"
            >
              <MessageSquare size={16} /> Send us a review
            </a>
          </div>
        </div>

        {/* Reviews grid */}
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-2">
          {REVIEWS.map((r) => (
            <article
              key={r.id}
              className="surface relative overflow-hidden p-7 flex flex-col"
              data-testid={`reviews-page-card-${r.id}`}
            >
              <Quote
                size={48}
                className="absolute -top-2 -left-2 text-accent opacity-10"
                aria-hidden="true"
              />
              <div className="relative z-10 flex-1 flex flex-col">
                <Stars count={r.rating} />
                <blockquote className="mt-4 text-lg leading-relaxed text-[color:var(--ft-text)] flex-1">
                  {r.text ? `“${r.text}”` : (
                    <span className="text-[color:var(--ft-muted)] italic">{RATING_ONLY_FALLBACK}</span>
                  )}
                </blockquote>
                <div className="mt-5 pt-4 border-t border-[color:var(--ft-border)]">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-sm text-[color:var(--ft-muted)] mt-0.5">
                    {r.role} · {r.relativeDate}
                  </div>
                  <div className="text-xs text-[color:var(--ft-muted)] mt-1">via {r.source}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="surface p-7 mt-10 max-w-3xl">
          <Star size={20} className="text-accent" fill="currentColor" strokeWidth={0} />
          <h2 className="font-display text-xl font-semibold mt-3">Trained with us? Share your experience.</h2>
          <p className="text-[color:var(--ft-muted)] mt-2">
            Honest feedback helps other Stuart and Martin County locals find the right coach. We appreciate every review.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              data-testid="reviews-page-write-google"
            >
              Leave a Google review <ExternalLink size={16} />
            </a>
            <a
              href={`mailto:${SITE.email}?subject=Review for Fitness Training 360`}
              className="btn btn-secondary"
              data-testid="reviews-page-email-review"
            >
              <MessageSquare size={16} /> Email us your review
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
